import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, {
  AUTH_EXPIRED_EVENT,
  clearAuthToken,
  clearConsoleAccessSession,
  getAuthToken,
  isConsoleAccessMode,
  setAuthToken,
} from "@/lib/api";
import { appBasePath } from "@/lib/routes";
import { configureMoney, currencyFromUser } from "@/lib/format";
import { normalizeUiPack } from "@/lib/uiPack";
import { rememberUiPack } from "@/lib/uiPackStorage";

const AuthContext = createContext(null);

function syncMoneyFromUser(nextUser) {
  if (!nextUser) return;
  configureMoney(currencyFromUser(nextUser));
}

function syncUiPackFromUser(nextUser) {
  if (nextUser?.company?.ui_pack) {
    rememberUiPack(nextUser.company.ui_pack);
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);
  const [consoleAccess, setConsoleAccess] = useState({
    active: false,
    platformUser: null,
    actingAsName: null,
  });
  const [exitingConsoleAccess, setExitingConsoleAccess] = useState(false);

  const applySessionMeta = useCallback((session, nextUser) => {
    const active = Boolean(session?.console_access) || isConsoleAccessMode();
    setConsoleAccess({
      active,
      platformUser: session?.platform_user || null,
      actingAsName: nextUser?.name || null,
    });
  }, []);

  const fetchUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
      setLoading(false);
      return null;
    }

    try {
      const { data } = await api.get("/user");
      setUser(data.user);
      syncMoneyFromUser(data.user);
      syncUiPackFromUser(data.user);
      applySessionMeta(data.session, data.user);
      return data.user;
    } catch {
      clearAuthToken();
      setUser(null);
      setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
      return null;
    } finally {
      setLoading(false);
    }
  }, [applySessionMeta]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    function onAuthExpired() {
      clearAuthToken();
      setUser(null);
      setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
      queryClient.clear();
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, [queryClient]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/login", { email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, login_token: data.login_token, user: data.user };
    }
    setAuthToken(data.token);
    setUser(data.user);
    syncMoneyFromUser(data.user);
    syncUiPackFromUser(data.user);
    setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
    return data.user;
  }, []);

  const verifyTwoFactor = useCallback(async (loginToken, code) => {
    const { data } = await api.post("/login/2fa", { login_token: loginToken, code });
    setAuthToken(data.token);
    setUser(data.user);
    syncMoneyFromUser(data.user);
    syncUiPackFromUser(data.user);
    setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
    return data.user;
  }, []);

  const updateContext = useCallback(async (branchId, counterId = null) => {
    setContextLoading(true);
    try {
      const { data } = await api.patch("/user/context", {
        branch_id: branchId,
        counter_id: counterId,
      });
      setUser(data.user);
      return data.user;
    } finally {
      setContextLoading(false);
    }
  }, []);

  const exitConsoleAccess = useCallback(async () => {
    setExitingConsoleAccess(true);
    try {
      try {
        await api.post("/logout");
      } catch {
        /* ignore */
      }
      clearConsoleAccessSession();
      setUser(null);
      setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
      queryClient.clear();
      const base = appBasePath();
      window.location.assign(base ? `${base}/console-access/closed` : "/console-access/closed");
    } finally {
      setExitingConsoleAccess(false);
    }
  }, [queryClient]);

  const logout = useCallback(async () => {
    if (isConsoleAccessMode()) {
      await exitConsoleAccess();
      return;
    }
    try {
      await api.post("/logout");
    } catch {
      // ignore — token may already be invalid
    } finally {
      clearAuthToken();
      setUser(null);
      setConsoleAccess({ active: false, platformUser: null, actingAsName: null });
      queryClient.clear();
    }
  }, [queryClient, exitConsoleAccess]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      contextLoading,
      consoleAccess,
      exitingConsoleAccess,
      uiPack: normalizeUiPack(user?.company?.ui_pack),
      login,
      verifyTwoFactor,
      logout,
      exitConsoleAccess,
      updateContext,
      refreshUser: fetchUser,
    }),
    [
      user,
      loading,
      contextLoading,
      consoleAccess,
      exitingConsoleAccess,
      login,
      verifyTwoFactor,
      logout,
      exitConsoleAccess,
      updateContext,
      fetchUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
